/**
 * ArgisLegal Dashboard — in-app User's Guide content (staff roles).
 *
 * Plain data so non-developers can edit wording without touching the page.
 * Keep it in step with the product: when a feature changes, update its section
 * here in the same commit. `path` must be an app route; the page only shows the
 * "Open" button when the reader's role can open that route.
 */

export type GuideRole = 'admin' | 'lawyer' | 'paralegal' | 'assistant';

export interface GuideSection {
  id: string;
  title: string;
  /** Who can do this. Omit = every staff role. */
  roles?: GuideRole[];
  /** App route the section is about (for the "Open" button). */
  path?: string;
  summary: string;
  steps?: string[];
  tips?: string[];
  /** Important caveat shown as a warning callout. */
  caution?: string;
  /** Marks features added in the most recent release. */
  isNew?: boolean;
}

export interface GuideChapter {
  id: string;
  title: string;
  intro: string;
  sections: GuideSection[];
}

export const GUIDE_VERSION = 'September 2026';

export const ROLE_LABELS: Record<GuideRole, string> = {
  admin: 'Firm admin',
  lawyer: 'Attorney',
  paralegal: 'Paralegal',
  assistant: 'Legal assistant',
};

const ADMIN: GuideRole[] = ['admin'];
const ADMIN_LAWYER: GuideRole[] = ['admin', 'lawyer'];
const STAFF_BUT_ASSISTANT: GuideRole[] = ['admin', 'lawyer', 'paralegal'];

export const GUIDE: GuideChapter[] = [
  // ---------------------------------------------------------------------------
  {
    id: 'getting-started',
    title: 'Getting started',
    intro: 'Signing in, finding your way around, and what your role lets you do.',
    sections: [
      {
        id: 'sign-in',
        title: 'Signing in and two-factor authentication',
        summary:
          'Staff sign in at your firm\'s ArgisLegal address with their email and password. Firm administrators — and anyone the firm requires — must also use two-factor authentication (2FA).',
        steps: [
          'Open the sign-in page and enter your work email and password.',
          'The first time you sign in with a temporary password you will be asked to choose your own. It must meet your firm\'s password policy.',
          'If 2FA is required, scan the QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, etc.) and enter the 6-digit code. Save the backup codes somewhere safe.',
          'On later sign-ins, enter the current 6-digit code from your app when asked.',
        ],
        tips: [
          'Clients never use this page — they sign in through the client portal. If a client lands here they are redirected to the portal sign-in.',
          'Several wrong passwords in a row temporarily lock the account. Wait for the lockout to pass or ask a firm admin.',
        ],
      },
      {
        id: 'navigation',
        title: 'Finding your way around',
        summary:
          'The left sidebar lists everything your role can open, grouped into Communications, Documents, billing, reports and administration. Items you do not have access to are hidden, not greyed out.',
        steps: [
          'Use the sidebar to move between areas. The Dashboard shows your caseload, upcoming events, tasks and recent activity.',
          'The bell icon at the top right shows notifications (new portal messages, assignments, signature events).',
          'The moon icon switches between light and dark mode.',
          'The blue stopwatch button in the corner starts a timer for billable work from any screen.',
          'Help & Support (bottom of the sidebar) has this guide, FAQs and a support request form.',
        ],
      },
      {
        id: 'roles',
        title: 'Roles: who can do what',
        summary:
          'Every staff member has one role. Your role decides which pages you see and which matters appear in your lists.',
        steps: [
          'Firm admin — everything, including team management, security, firm settings, subscription, deleting records and financial reports. Sees every matter.',
          'Attorney — cases, clients, documents, billing (including creating invoices), trust entries, privilege log and reports. Sees matters they created or are assigned to.',
          'Paralegal — cases, clients, documents, tasks, calendar, time and reports. Sees only matters assigned to them.',
          'Legal assistant — day-to-day work on assigned matters (documents, tasks, calendar, time). No reports.',
          'A firm admin can mark an attorney as "Managing attorney" or a paralegal as "Office manager". That person keeps their role but gets full firm-admin access (and must use 2FA).',
        ],
        tips: [
          'If you cannot see a matter you expect, you are probably not assigned to it. Ask a firm admin or the responsible attorney to add you.',
        ],
      },
      {
        id: 'profile',
        title: 'Your profile and password',
        path: '/profile',
        summary: 'Update your name, contact details and password.',
        steps: [
          'Click your avatar (top right) or open Profile.',
          'Edit your details and save.',
          'To change your password, use Change Password and enter your current password first.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'firm-setup',
    title: 'Firm setup (firm admins)',
    intro: 'One-time and occasional setup: your firm profile, team, security and court forms.',
    sections: [
      {
        id: 'first-login',
        title: 'Accepting your firm invitation',
        roles: ADMIN,
        summary:
          'When ArgisTech creates your firm, the first administrator receives an invitation link by email (or directly from ArgisTech).',
        steps: [
          'Open the invitation link and choose your password.',
          'Sign in and set up two-factor authentication — it is mandatory for firm admins.',
          'Work through the rest of this chapter: firm profile, team, security settings and (if you use them) court forms.',
        ],
      },
      {
        id: 'firm-profile',
        title: 'Firm profile, branding and plan limits',
        roles: ADMIN,
        path: '/tenant',
        summary:
          'The Tenant page shows your firm\'s details, branding, enabled features and plan limits (users, cases, storage).',
        steps: [
          'Open Tenant from the sidebar.',
          'Overview shows your plan, status and usage. Branding sets your firm name, logo and colours.',
          'Features and Limits show what your plan includes and how much you have used.',
        ],
        tips: [
          'Trial and pilot firms see their trial end date here. After the trial and a short grace period, a firm that has not upgraded becomes read-only — you can still view everything, but not change it, until the plan is renewed.',
        ],
      },
      {
        id: 'team',
        title: 'Adding and managing staff',
        roles: ADMIN,
        path: '/users',
        summary: 'Invite staff, set their role, and deactivate people who leave — without losing their history.',
        steps: [
          'Open Team Management and click Add Team Member.',
          'Enter name and email, choose the role (Attorney, Paralegal, Legal assistant or Admin).',
          'For an attorney who also runs the firm, tick Managing attorney; for a paralegal who runs the office, tick Office manager.',
          'Save. The person receives a temporary password or invitation; they choose their own password on first sign-in.',
          'To remove someone, deactivate or delete them. Their past work (notes, activities, time) stays on the matters.',
        ],
        caution:
          'Client portal accounts are not managed here — they are created from the client record (see "Giving a client portal access").',
      },
      {
        id: 'security',
        title: 'Security settings',
        roles: ADMIN,
        path: '/security',
        summary: 'Firm-wide rules for sign-in, document release, e-signature and online payments.',
        steps: [
          'Require 2FA for all users — recommended for every firm.',
          'Require attorney review before download / send — documents must be marked "Attorney reviewed" before staff can send them out or share them.',
          'Enable native e-sign for portal clients — lets you request signatures from clients through the portal.',
          'Enable portal Pay on invoices — lets clients pay invoices online from the portal (requires the payment gateway to be configured by ArgisTech).',
          'Portal calls — lets staff ring clients who are signed in to the portal (voice or video). Allow call recording adds the option to record, which the client must agree to on every call.',
          'Password policy, session timeout and lockout settings.',
        ],
      },
      {
        id: 'court-forms-setup',
        title: 'Setting up court forms',
        roles: ADMIN,
        path: '/court-forms',
        isNew: true,
        summary:
          'Choose your practice areas and jurisdictions once; ArgisLegal then offers matching case types and adds the right official forms to new matters automatically.',
        steps: [
          'Open Court Forms → Firm setup.',
          'Tick your practice areas (for example Immigration, Family law, Guardianship).',
          'Choose jurisdictions. Immigration is federal, so USCIS and EOIR forms are included automatically; add the states you practise in.',
          'Leave "Automatically add the case type\'s required forms" switched on if you want forms added when a matter is opened. Click Save settings.',
          'Click Enable recommended forms. This switches on every form used by the recommended packets for your areas and states.',
          'Optional: open Case type packets to change which forms a case type gets, and which are added automatically versus offered as optional.',
          'Optional: in Form library, switch individual forms on or off.',
        ],
        tips: [
          'The library currently contains USCIS, EOIR (Immigration Court / BIA) and California Judicial Council forms. States with no forms yet show 0.',
          'Which forms you can choose depends on your plan: Basic includes Federal forms; Professional includes Federal forms plus one state; Enterprise includes Federal forms and every state. Firm setup shows what your plan includes — contact ArgisTech support to add states.',
          'ArgisTech adds new forms and newer editions to the shared library. A newer edition of a form you already use becomes current automatically; a brand-new form appears in Form library for you to switch on.',
          'Recommended packets are a starting point prepared for common matters — they are not legal advice. Review them for your practice.',
        ],
      },
      {
        id: 'data-portability',
        title: 'Importing and backing up firm data',
        roles: ADMIN,
        path: '/settings',
        summary:
          'Settings → Data Portability lets you import contacts and matters from CSV (for example from another practice-management system), import a case package, and download a firm backup.',
        steps: [
          'Open Settings → Data Portability.',
          'Download the CSV template, fill it in, and run a dry run first to see what would be imported.',
          'Use Firm backup to download a ZIP of your contacts and matters.',
        ],
        caution: 'Backups and exports contain client information. Store them securely and never email them unencrypted.',
      },
      {
        id: 'subscription',
        title: 'Subscription and trial status',
        roles: ADMIN,
        path: '/subscription',
        summary: 'See your current plan and, where online billing is enabled, upgrade or manage your subscription.',
        tips: [
          'If your firm has become read-only after a trial, contact ArgisTech support — your data is intact and full access is restored as soon as the plan is renewed.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'clients',
    title: 'Clients and intake',
    intro: 'Recording new clients, moving leads through intake, and giving clients portal access.',
    sections: [
      {
        id: 'add-client',
        title: 'Adding a client (intake)',
        path: '/clients',
        summary:
          'Every matter belongs to a client. The client record captures contact details and the intake interview.',
        steps: [
          'Open Clients and click New Client.',
          'Enter the client\'s details. Intake date and time are filled in automatically and can be back-dated for a phone intake taken earlier.',
          'Choose who conducted the intake, the preferred language, and complete the address including ZIP.',
          'Record how the client found you (referral source) — it feeds the "New matters & intake source" report.',
          'Save.',
        ],
        tips: [
          'If you are told an email "already exists", the message names who holds it inside your firm. Addresses from deleted clients and staff are released automatically.',
        ],
      },
      {
        id: 'pipeline',
        title: 'Intake pipeline',
        path: '/intake-pipeline',
        summary:
          'A board that tracks prospective clients from first contact to active client: New lead → Contacted → Consultation scheduled → Retainer sent → Signed → Active client.',
        steps: [
          'Open Intake Pipeline.',
          'Drag a card to the next column as the client progresses, or open it to see details.',
        ],
      },
      {
        id: 'portal-access',
        title: 'Giving a client portal access',
        roles: ADMIN_LAWYER,
        path: '/clients',
        summary:
          'The client portal lets clients message the firm, view documents you share, sign documents and pay invoices.',
        steps: [
          'Open the client and choose Enable portal access & create login.',
          'Copy the invitation link and send it to the client (or let the system email it). The client sets their own password.',
          'Only documents marked for the client, on their matters, and cleared for release appear in their portal.',
        ],
        caution:
          'Moving a document to a different client\'s matter automatically moves it out of the previous client\'s portal and resets it to Draft, so it must be reviewed again before the new client can see it.',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'cases',
    title: 'Cases (matters)',
    intro: 'Opening matters, finding them, and working inside the case folder.',
    sections: [
      {
        id: 'new-case',
        title: 'Opening a new matter',
        path: '/cases',
        summary:
          'Use New Case Intake for a guided intake (parties, conflict check, questionnaire), or create a case directly.',
        steps: [
          'Open Cases and click New Case Intake.',
          'Choose the client, enter the matter title and details, and add the other parties. A conflict check runs against your existing clients and parties.',
          'Choose the Case Type. If your firm has set up court forms, pick from the list (for example "Naturalization (N-400)" or "Dissolution of marriage"); you can still type your own.',
          'Assign the responsible attorney and any staff.',
          'Save. If the case type has a court-form packet, the required forms are added to the matter\'s Court Forms folder as drafts and a message tells you how many.',
        ],
        tips: [
          'Choosing a case type from the list also sets the jurisdiction for state case types.',
          'Continue Intake in the case folder reopens the questionnaire later.',
        ],
      },
      {
        id: 'case-list',
        title: 'Finding matters',
        path: '/cases',
        summary:
          'The Cases list has search, status, priority, intake and assignment filters, plus quick queues.',
        steps: [
          'Type in Search cases to find by title, number or client.',
          'Use the quick filters: New intake queue, Unassigned, Awaiting signature.',
          'Admins and attorneys can assign an unassigned matter straight from the list.',
        ],
        tips: [
          'Attorneys see matters they created or are assigned to; paralegals and assistants see matters assigned to them; firm admins see all. This is deliberate — it keeps an ethical wall between matters.',
        ],
      },
      {
        id: 'case-folder',
        title: 'Inside the case folder',
        summary:
          'Open a matter to reach its folder. The left panel summarises the case; the tabs hold everything else.',
        steps: [
          'Documents — files on the matter, with review status, signature status and actions.',
          'Activity — the activity log and full matter timeline (see the next chapter).',
          'Notes — internal notes (case, client or document notes; can be marked confidential).',
          'Properties — real estate and other property on the matter.',
          'Privilege Log — privilege entries, exportable to CSV.',
          'Workflows — apply a workflow template (a step-by-step checklist) to the matter.',
          'Calls — calls logged against the matter.',
          'Court Forms — official forms for this matter\'s case type.',
          'Toolbar: Upload, Add Existing (file a library document to this matter), Export Case File, Import Template, Continue Intake, Add Note.',
        ],
      },
      {
        id: 'sol',
        title: 'Statute of limitations and deadlines',
        roles: STAFF_BUT_ASSISTANT,
        summary:
          'Docket the statute of limitations on a matter so it appears on the calendar and in the deadlines report.',
        steps: [
          'In the case summary, click Calculate & save SOL.',
          'Choose the SOL preset for the jurisdiction and claim, enter the incident / accrual date, and save. The expiry is stored on the matter and added to the calendar.',
          'For court deadlines, use the date calculator on the Calendar (calendar or business days, forward or backward).',
        ],
        caution: 'Presets are illustrative starting points. Always verify the limitation period and any tolling for your case.',
      },
      {
        id: 'engagement',
        title: 'Retainers, e-signature and engagement',
        roles: ADMIN_LAWYER,
        summary:
          'Send an engagement letter or other eligible document for the client to sign in the portal, then confirm the engagement.',
        steps: [
          'Mark the document as Attorney reviewed (document actions → Review).',
          'Choose Request signature. The dialog lists anything blocking the request (for example, the client has no portal access yet) with one-click fixes.',
          'The client signs in the portal. The signed PDF and a signing certificate are added to the matter\'s Executed folder, and the original is locked.',
          'Signing does not engage the firm automatically — an attorney or admin clicks Confirm engagement.',
        ],
        caution:
          'Wills, powers of attorney, notarised documents, deeds, court originals and similar documents cannot be e-signed this way and are blocked on purpose.',
      },
      {
        id: 'export-case',
        title: 'Exporting a case file',
        roles: STAFF_BUT_ASSISTANT,
        summary: 'Export Case File creates a package of the matter (documents and data) for transfer or archiving.',
        caution: 'Case files contain client information. Handle and store exports securely.',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'activity',
    title: 'Activity log and timeline',
    intro: 'A dated record of everything that happens on a matter — the record you rely on if the file is ever questioned.',
    sections: [
      {
        id: 'log-activity',
        title: 'Logging an activity',
        isNew: true,
        summary:
          'Record filings, hearings, client communications, research and other work on the matter\'s Activity tab.',
        steps: [
          'Open the matter → Activity → Log activity.',
          'Choose the activity type (Court: filing, hearing, court appearance · Client: call, email, meeting, letter, message · Opposing counsel / third party · Litigation: deposition, discovery, negotiation · Internal: research, drafting, file review).',
          'Set When it happened (it defaults to now and cannot be in the future), the subject and details.',
          'For hearings and filings, add the court, department, judge, reference number and the outcome or ruling.',
          'Enter a Duration and tick Create billable time entry to turn it into billable time in one step.',
          'Tick Attorney-client privileged or Confidential where appropriate, then save.',
        ],
        tips: [
          'Use the group chips (Court, Client communication, …) to filter the log.',
          'Activities never appear in the client portal.',
        ],
      },
      {
        id: 'next-date',
        title: 'Continuances and next dates',
        isNew: true,
        summary: 'When a hearing is continued or a follow-up is set, record the next date on the activity.',
        steps: [
          'In the activity, set Next date (continuance / follow-up).',
          'Keep Add next date to the firm calendar ticked. The event is created on the calendar, linked to the matter.',
          'Once it is on the calendar, the activity shows "Next date is on the firm calendar".',
        ],
      },
      {
        id: 'edit-activity',
        title: 'Editing and removing activities',
        isNew: true,
        summary:
          'The author (or a firm admin) can edit an activity. Nothing is overwritten: previous values are kept in the activity\'s edit history.',
        steps: [
          'Click the pencil on the activity, change it, and Save changes. A history icon appears showing how many times it was edited.',
          'You can bill time later: enter the duration, tick Create billable time entry, and save. Time is billed once.',
          'Removing an activity hides it from the log but keeps it in the audit trail.',
        ],
      },
      {
        id: 'timeline',
        title: 'Full matter timeline',
        isNew: true,
        summary:
          'Switch the Activity tab to Full timeline to see activities, notes, calls, documents and calendar events together, newest first.',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'documents',
    title: 'Documents and forms',
    intro: 'Templates, court forms, matter documents, the Matter Drive, and review before anything leaves the firm.',
    sections: [
      {
        id: 'templates',
        title: 'Firm templates and mail-merge',
        path: '/templates',
        summary:
          'Store your firm\'s standard documents (retainers, letters, wills) as templates. When imported into a matter, {{placeholders}} are filled from the client and case.',
        steps: [
          'Open Firm Templates → Upload Template and upload a Word document containing placeholders such as {{Client_Name}} or {{Case_Caption}}.',
          'From a matter, click Import Template, choose the template, and it is copied into the matter with the placeholders filled in.',
          'If client or case details change later, use Refresh merge on the document.',
        ],
        tips: ['The placeholder list (shortcode guide) is available from your firm admin or ArgisTech support.'],
      },
      {
        id: 'court-forms-use',
        title: 'Court forms on a matter',
        path: '/court-forms',
        isNew: true,
        summary:
          'Official USCIS, EOIR and state court forms, added to matters by case type or by hand. Each form is copied into the matter as a draft for attorney review.',
        steps: [
          'Open the matter → Court Forms. You will see the packet for the matter\'s case type: which forms are already on the matter, which are optional, and any that are not in the library yet.',
          'Tick the forms you want and click Add selected forms, or use Add from library to search for any enabled form by number or title.',
          'Added forms appear in the matter\'s Court Forms folder and in Documents. Open them to fill in.',
          'Court Forms in the sidebar lets you browse and preview every form your firm has enabled.',
        ],
        caution:
          'Agencies can reject outdated editions. Each form shows its edition date; if a matter has an older edition than the library, it is flagged "Newer edition available". Check the agency\'s website before filing.',
      },
      {
        id: 'matter-documents',
        title: 'Uploading and filing documents',
        path: '/documents',
        summary:
          'Matter Documents lists documents across matters. Upload to a matter, or file an unfiled document to the right matter.',
        steps: [
          'Upload from the matter\'s folder (Upload) or from Matter Documents (choose the matter while uploading).',
          'For an unfiled document, click its "Unfiled" chip to assign it to a matter. From inside a matter, Add Existing pulls in a document from the library.',
          'Use document actions to view, edit, download, Bates-stamp, refresh merge, change review status or request a signature.',
        ],
        tips: ['HTML, SVG and script files are rejected for security. Use PDF or Office formats.'],
      },
      {
        id: 'review-status',
        title: 'Review status: Draft → Attorney reviewed → Sent → Locked',
        summary:
          'Every document carries a review status. It controls what can leave the firm and what clients can see.',
        steps: [
          'Draft — new and generated documents (including court forms) start here.',
          'Attorney reviewed — an attorney has checked it. If your firm requires review before release, only reviewed documents can be sent, shared or signed.',
          'Sent — it has gone out.',
          'Locked — the original of a signed document; it cannot be changed.',
        ],
      },
      {
        id: 'drive',
        title: 'Matter Drive and share links',
        path: '/drive',
        summary:
          'A folder view of each matter (Pleadings, Discovery, Correspondence, Evidence, Court Filings, Notes, Executed, Court Forms). Drag files and folders to reorganise.',
        steps: [
          'Open Matter Drive, or Open in Matter Drive from a case folder.',
          'Create folders, drag and drop to move, and upload into a folder.',
          'Create a share link for a document or folder to give an outside party time-limited, read-only access.',
        ],
        caution: 'Confidential and privileged documents cannot be shared by link.',
      },
      {
        id: 'privilege',
        title: 'Privilege log, Bates numbering and captions',
        roles: ADMIN_LAWYER,
        summary: 'Litigation tools on the matter.',
        steps: [
          'Privilege Log tab — record withheld documents and export the log to CSV for production.',
          'Bates (document actions) — stamp Bates numbers on PDFs.',
          'Auto-caption (Documents tab) — build the case caption from the parties for pleadings and merge fields.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'communications',
    title: 'Communications',
    intro: 'Messaging clients securely, sending email from the platform, and logging calls.',
    sections: [
      {
        id: 'portal-inbox',
        title: 'Portal Inbox (secure client messages)',
        path: '/portal-inbox',
        summary:
          'Secure message threads with clients who have portal access. Both sides are notified by email that a message is waiting; the message itself stays in the portal.',
        steps: [
          'Open Portal Inbox, choose a conversation, and reply. Link it to a matter where relevant.',
          'Unread messages show a badge.',
        ],
      },
      {
        id: 'mail',
        title: 'Mail: sending and filing email',
        path: '/mail',
        summary:
          'Send email linked to a client or matter, connect your Gmail or Microsoft 365 mailbox, and file incoming mail to matters.',
        steps: [
          'Compose from Mail. Every message must be linked to a case or a client; a note is added to the matter when it is sent.',
          'Connect your Gmail or Microsoft 365 mailbox to send from your own address and see your inbox. Admins can connect shared firm mailboxes.',
          'Inbox → File to case files an email to a matter. Filing rules (by sender or subject) can file matching mail automatically.',
          'Add a privilege tag to privileged correspondence.',
        ],
      },
      {
        id: 'calls',
        title: 'Logging calls',
        summary: 'Record a phone call on the matter\'s Calls tab, with optional billable time.',
        steps: ['Open the matter → Calls → Log call.', 'Enter direction, numbers, duration and notes; keep Create billable time entry ticked to bill it.'],
        tips: ['For a fuller record (contact name, privilege flag), log the call as a Client call on the Activity tab instead.'],
      },
      {
        id: 'portal-calls',
        title: 'Calling a client through the portal',
        isNew: true,
        summary:
          'Ring a client who is signed in to the client portal and talk by voice or video, right from the matter. No phone numbers, no extra app — audio and video go browser to browser, encrypted.',
        steps: [
          'Open the matter (or the client) → Calls → Call in portal. The button appears once a firm administrator turns on Portal calls under Security.',
          'The dialog tells you whether the client is in the portal right now. It rings on their screen for 45 seconds, and only while they have the portal open — if they are not signed in, send a portal message first.',
          'Choose Video call if you want cameras on, and — if your firm allows it — Ask the client to allow recording.',
          'Click Call and allow your browser to use the microphone (and camera). Use the buttons to mute, turn your camera off, or hang up.',
          'When the call ends, add notes and, on a matter, tick Create billable time entry. The call is logged automatically in the Call log and, on a matter, as a privileged Client call on the Activity tab.',
        ],
        tips: [
          'Recording only starts if the client picks "Answer and allow recording". If they say no, the call carries on unrecorded.',
          'Recordings are saved to the matter\'s Call Recordings folder as confidential, privileged documents. They are never shown in the portal and cannot be shared by link.',
          'Keep the call window open until "Recording saved" appears — the file uploads when you hang up.',
          'Clients can also press Request a call back in the portal; it arrives in the Portal Inbox.',
        ],
        caution:
          'Some states require every party\'s consent to record a call. The consent prompt covers the client, but tell anyone else in the room, and check your jurisdiction\'s rules.',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'calendar-tasks',
    title: 'Calendar and tasks',
    intro: 'Hearings, meetings, deadlines and to-dos.',
    sections: [
      {
        id: 'calendar',
        title: 'Calendar',
        path: '/calendar',
        summary: 'Firm calendar in month, week and list views. Each person\'s events have their own colour.',
        steps: [
          'Create an event and link it to a matter and client where relevant (hearing, deposition, mediation, filing deadline, meeting…).',
          'Rooms & Zoom books meeting rooms and video meetings.',
          'Use the date calculator to work out court deadlines in calendar or business days.',
        ],
        tips: ['Next dates from the Activity log and saved statutes of limitations appear here automatically.'],
      },
      {
        id: 'tasks',
        title: 'Tasks',
        path: '/tasks',
        summary: 'Assign to-dos with due dates and priorities, linked to matters.',
        steps: ['Open Tasks → Create Task, assign it, set a due date and link the matter.', 'Filter by assignee, status or matter. Overdue tasks are highlighted and counted in the workload report.'],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'billing',
    title: 'Time and billing',
    intro: 'Capturing time, invoicing clients and following up on payment.',
    sections: [
      {
        id: 'timer',
        title: 'Tracking time',
        path: '/time-entries',
        summary: 'Use the stopwatch or enter time by hand. Time can also be created from activities and calls.',
        steps: [
          'Click the blue stopwatch (bottom right) to start, pause and stop a timer while you work; choose the matter when you save.',
          'Or open Time Entries to add or correct entries: matter, task type, description, duration, rate and whether it is billable.',
        ],
      },
      {
        id: 'invoices',
        title: 'Invoices, payments and AR aging',
        roles: ['admin', 'lawyer', 'paralegal', 'assistant'],
        path: '/invoices',
        summary:
          'Create invoices from unbilled time and expenses, send them, record payments and track what is overdue. Creating and sending invoices requires attorney or admin billing rights.',
        steps: [
          'Billing shows unbilled time and expenses by matter. Create an invoice from there.',
          'Invoices → All invoices: send by email (with a Pay link if online payment is enabled), mark paid, or apply a payment.',
          'Payments lists payments received; AR aging shows unpaid balances by how overdue they are.',
          'Recurring schedules create invoices automatically on a set frequency.',
        ],
      },
      {
        id: 'expenses',
        title: 'Expense approvals',
        roles: ADMIN_LAWYER,
        path: '/expense-approvals',
        summary: 'Approve, reject or return expenses before they are billed to the client.',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'reports',
    title: 'Reports',
    intro: 'Printable reports for the firm, plus charts and workload views.',
    sections: [
      {
        id: 'report-center',
        title: 'Report Center',
        roles: STAFF_BUT_ASSISTANT,
        path: '/report-center',
        isNew: true,
        summary:
          'Run firm reports as a table and download them as PDF, Excel or CSV. Results follow your access: attorneys and paralegals see their own matters and time; firm admins see the whole firm.',
        steps: [
          'Open Report Center and pick a report on the left.',
          'Set the filters (period, attorney, practice area, jurisdiction, status, office area…) and click Run report.',
          'Read the summary tiles and group totals, sort the table by clicking a column, then download PDF, Excel or CSV.',
        ],
        tips: [
          'Caseload: Cases by attorney · Cases by practice area · Workload by office area (staff role) · Open matter aging (flags idle files) · New matters & intake source.',
          'Deadlines: Upcoming hearings & deadlines (including statutes of limitations).',
          'Activity: Activity log · Client communications log.',
          'Time & billing: Hours by timekeeper · Accounts receivable aging · Billing & collections (AR and billing reports: firm admins and billing attorneys).',
        ],
        caution:
          'Every download is recorded in the audit trail. PDFs are marked confidential — store and share exports as you would the client file.',
      },
      {
        id: 'charts',
        title: 'Report Charts, Staff Workload and Analytics',
        roles: STAFF_BUT_ASSISTANT,
        path: '/reports',
        summary:
          'Report Charts shows trends and distributions. Firm admins also have Staff Workload (with drill-down to each person\'s matters and tasks) and Analytics.',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'portal-clients',
    title: 'What your clients see',
    intro: 'A short tour of the client portal, so you can guide clients through it.',
    sections: [
      {
        id: 'portal-tour',
        title: 'The client portal',
        summary:
          'Clients sign in at the portal (not the staff sign-in) with the password they chose from your invitation.',
        steps: [
          'Dashboard — their matters and recent updates.',
          'Messages — secure conversation with the firm.',
          'Documents — only documents the firm has released to them.',
          'Signatures — documents waiting for their e-signature, with an electronic-records consent step and password re-check.',
          'Invoices — view and, if enabled, pay online.',
          'Calls — if your firm uses portal calls, the portal rings when you call them. They answer in the browser, choose whether the call may be recorded, and can press Request a call back at any time.',
        ],
        tips: [
          'Clients never see internal notes, the activity log, privilege entries, other clients\' documents or staff pages.',
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    intro: 'Quick answers to the questions we hear most.',
    sections: [
      {
        id: 'read-only',
        title: '"This firm is read-only"',
        summary:
          'Your firm\'s trial or plan has ended. Everything is still there and viewable, but changes are blocked until the plan is renewed.',
        steps: ['A firm admin should contact ArgisTech support. Full access returns as soon as the plan is renewed — no data is lost.'],
      },
      {
        id: 'cant-see',
        title: 'I cannot see a matter, report or page',
        summary:
          'Pages and matters follow your role and assignments. Ask a firm admin to assign you to the matter or to check your role.',
      },
      {
        id: 'locked-out',
        title: 'Locked out or lost my 2FA device',
        summary:
          'After several failed sign-ins the account locks for a short time. If you lost your authenticator, use a backup code or ask your firm admin to reset your 2FA.',
      },
      {
        id: 'court-form-missing',
        title: 'A court form is missing or marked "Not enabled"',
        summary:
          '"Missing from library" means ArgisTech has not added that form yet — tell support which form you need. "Not enabled" means your firm admin has not switched it on in Court Forms → Form library.',
      },
      {
        id: 'call-wont-connect',
        title: 'A portal call will not ring or connect',
        summary: 'Most call problems are the client not being in the portal, or a browser permission.',
        steps: [
          'Doesn\'t ring: the client must be signed in to the portal with the page open. Ask them to sign in, then call again.',
          'Rings but no sound: both sides must allow the microphone in the browser (look for the camera/microphone icon in the address bar).',
          '"Could not connect": some strict office or hotel networks block calls. Try again from another network, or phone the client instead.',
          'Use a current Chrome, Edge, Firefox or Safari. On phones, keep the portal page in front during the call.',
        ],
      },
      {
        id: 'support',
        title: 'Getting help',
        path: '/help',
        summary:
          'Use the support request form on the Help & Support page. Include the matter number and what you were doing — never paste client personal details into a support request.',
      },
    ],
  },
];
